import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Send,
  History,
  Loader2,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Mail,
  Smartphone,
  Bell,
  MessageSquare,
  Users,
  Radio,
  ChevronDown,
  ChevronUp,
  Copy,
  X,
  Search,
  UserPlus,
  UserMinus,
} from 'lucide-react';
import { useNotificationTemplates, useNotificationHistory } from '../hooks/useNotifications';
import { useUserList } from '../hooks/useUsers';
import { notificationsService } from '../services/notifications';
import type {
  NotificationChannel,
  NotificationLogEntry,
  BatchSummary,
  SendToUserRequest,
  SendToMultipleRequest,
  SendBroadcastRequest,
} from '../types/notifications';
import type { UserListItem } from '../types/user';
import { cn, formatDateTime, formatNumber, showToast } from '../utils/helpers';
import { Badge } from '../components/ui';

// ============================================================================
// Constants
// ============================================================================

const PAGE_SIZE = 20;

const ALL_CHANNELS: { key: NotificationChannel; label: string; icon: React.ElementType }[] = [
  { key: 'PUSH', label: 'Push', icon: Smartphone },
  { key: 'SMS', label: 'SMS', icon: MessageSquare },
  { key: 'EMAIL', label: 'Email', icon: Mail },
  { key: 'IN_APP', label: 'In-App', icon: Bell },
];

type SendMode = 'users' | 'broadcast';

interface SelectedUser {
  id: number;
  name: string | null;
  email: string | null;
  profileImage: string | null;
}

const USER_PICKER_PAGE_SIZE = 10;

type TabKey = 'send' | 'history';

// ============================================================================
// Badge helpers
// ============================================================================

function statusBadge(status: string): { variant: 'success' | 'warning' | 'error' | 'info' | 'muted' } {
  const map: Record<string, 'success' | 'warning' | 'error' | 'info' | 'muted'> = {
    sent: 'info',
    delivered: 'success',
    failed: 'error',
    pending: 'warning',
    read: 'success',
  };
  return { variant: map[status?.toLowerCase()] || 'muted' };
}

function channelBadge(channel: NotificationChannel): { variant: 'success' | 'warning' | 'error' | 'info' | 'muted' } {
  const map: Record<string, 'success' | 'warning' | 'error' | 'info' | 'muted'> = {
    PUSH: 'info',
    SMS: 'warning',
    EMAIL: 'muted',
    IN_APP: 'success',
  };
  return { variant: map[channel] || 'muted' };
}

// ============================================================================
// Shared sub-components (matching Webhooks patterns)
// ============================================================================

interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  loading?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon: Icon, color, bgColor, loading }) => (
  <div className="card p-4">
    <div className="flex items-center gap-3">
      <div className={cn('p-2 rounded-xl flex-shrink-0', bgColor)}>
        <Icon className={cn('h-5 w-5', color)} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground truncate">{label}</p>
        {loading ? (
          <div className="h-7 w-16 bg-muted/30 rounded animate-pulse mt-0.5" />
        ) : (
          <p className="text-xl font-bold text-foreground">{value}</p>
        )}
      </div>
    </div>
  </div>
);

const Pagination: React.FC<{
  page: number;
  totalPages: number;
  total: number;
  onPageChange: (page: number) => void;
  loading?: boolean;
}> = ({ page, totalPages, total, onPageChange, loading }) => {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
      <p className="text-xs text-muted-foreground">Page {page} of {totalPages} ({formatNumber(total)} total)</p>
      <div className="flex items-center gap-1">
        <button onClick={() => onPageChange(page - 1)} disabled={page <= 1 || loading} className="p-1.5 rounded-lg hover:bg-primary/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
          <ChevronLeft className="h-4 w-4 text-foreground" />
        </button>
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          let pageNum: number;
          if (totalPages <= 5) pageNum = i + 1;
          else if (page <= 3) pageNum = i + 1;
          else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
          else pageNum = page - 2 + i;
          return (
            <button
              key={pageNum}
              onClick={() => onPageChange(pageNum)}
              disabled={loading}
              className={cn('w-8 h-8 text-xs rounded-lg transition-colors', pageNum === page ? 'bg-primary/20 text-primary font-semibold' : 'text-muted-foreground hover:bg-primary/10')}
            >
              {pageNum}
            </button>
          );
        })}
        <button onClick={() => onPageChange(page + 1)} disabled={page >= totalPages || loading} className="p-1.5 rounded-lg hover:bg-primary/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
          <ChevronRight className="h-4 w-4 text-foreground" />
        </button>
      </div>
    </div>
  );
};

const DetailSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="pt-2 mt-2 first:mt-0 first:pt-0">
    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">{title}</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">{children}</div>
  </div>
);

const DetailRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => {
  if (value == null || value === '') return null;
  return (
    <div className="flex items-baseline justify-between gap-2 py-0.5">
      <span className="text-[10px] text-muted-foreground whitespace-nowrap">{label}</span>
      <span className="text-[10px] text-foreground text-right break-all">{value}</span>
    </div>
  );
};

// ============================================================================
// Confirmation Modal
// ============================================================================

const ConfirmModal: React.FC<{
  open: boolean;
  title: string;
  children: React.ReactNode;
  confirmLabel: string;
  confirmVariant?: 'primary' | 'danger';
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ open, title, children, confirmLabel, confirmVariant = 'primary', loading, onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-card border border-border rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          <button onClick={onCancel} className="p-1 rounded-lg hover:bg-primary/10 transition-colors">
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>
        <div className="text-xs text-muted-foreground space-y-2 mb-6">{children}</div>
        <div className="flex items-center justify-end gap-2">
          <button onClick={onCancel} disabled={loading} className="px-4 py-2 text-xs font-medium text-muted-foreground rounded-lg hover:bg-primary/10 transition-colors">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={cn(
              'px-4 py-2 text-xs font-medium rounded-lg transition-colors flex items-center gap-2',
              confirmVariant === 'danger'
                ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                : 'bg-primary/20 text-primary hover:bg-primary/30'
            )}
          >
            {loading && <Loader2 className="h-3 w-3 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// User Picker Panel
// ============================================================================

const UserPickerPanel: React.FC<{
  selectedUsers: SelectedUser[];
  onAdd: (user: SelectedUser) => void;
  onRemove: (userId: number) => void;
}> = ({ selectedUsers, onAdd, onRemove }) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pickerPage, setPickerPage] = useState(1);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPickerPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const userListParams = useMemo(() => ({
    search: debouncedSearch || undefined,
    limit: USER_PICKER_PAGE_SIZE,
    offset: (pickerPage - 1) * USER_PICKER_PAGE_SIZE,
  }), [debouncedSearch, pickerPage]);

  const { items: users, loading: usersLoading, totalPages: usersTotalPages } = useUserList(userListParams);

  const selectedIds = useMemo(() => new Set(selectedUsers.map((u) => u.id)), [selectedUsers]);

  return (
    <div className="card flex flex-col h-full">
      {/* Header with selected count */}
      <div className="p-3 border-b border-border flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Select Users</h3>
          {selectedUsers.length > 0 && (
            <Badge variant="info" size="sm">{selectedUsers.length} selected</Badge>
          )}
        </div>
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search users by name, email..."
            className="w-full pl-8 pr-8 py-2 text-sm bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2">
              <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
            </button>
          )}
        </div>
      </div>

      {/* Selected users chips */}
      {selectedUsers.length > 0 && (
        <div className="px-3 pt-2 pb-1 border-b border-border flex-shrink-0">
          <div className="flex flex-wrap gap-1.5 max-h-20 overflow-y-auto scrollbar-thin">
            {selectedUsers.map((u) => (
              <span
                key={u.id}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] bg-primary/15 text-primary border border-primary/30 rounded-full"
              >
                {u.name || u.email || `#${u.id}`}
                <button onClick={() => onRemove(u.id)} className="hover:text-red-400 transition-colors">
                  <X className="h-2.5 w-2.5" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* User list */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {usersLoading && users.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 text-primary animate-spin" />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground text-xs">No users found</div>
        ) : (
          <div className="divide-y divide-border">
            {users.map((user) => {
              const isSelected = selectedIds.has(user.id);
              return (
                <button
                  key={user.id}
                  onClick={() =>
                    isSelected
                      ? onRemove(user.id)
                      : onAdd({ id: user.id, name: user.name, email: user.email, profileImage: user.profileImage })
                  }
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors group',
                    isSelected
                      ? 'bg-primary/10'
                      : 'hover:bg-primary/5'
                  )}
                >
                  {/* Avatar */}
                  <div className="w-7 h-7 rounded-full bg-muted/30 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {user.profileImage ? (
                      <img src={user.profileImage} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-[10px] font-semibold text-muted-foreground">
                        {(user.name || user.email || '?').charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-foreground truncate">
                      {user.name || 'No name'}
                      <span className="text-muted-foreground ml-1">#{user.id}</span>
                    </p>
                    <p className="text-[10px] text-muted-foreground truncate">{user.email || user.phone || 'No contact'}</p>
                  </div>
                  {/* Add/Remove indicator */}
                  <div className="flex-shrink-0">
                    {isSelected ? (
                      <UserMinus className="h-3.5 w-3.5 text-red-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                    ) : (
                      <UserPlus className="h-3.5 w-3.5 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {usersTotalPages > 1 && (
        <div className="px-3 py-2 border-t border-border flex items-center justify-between flex-shrink-0">
          <span className="text-[10px] text-muted-foreground">Page {pickerPage} of {usersTotalPages}</span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPickerPage((p) => Math.max(1, p - 1))}
              disabled={pickerPage <= 1 || usersLoading}
              className="p-1 rounded hover:bg-primary/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="h-3.5 w-3.5 text-foreground" />
            </button>
            <button
              onClick={() => setPickerPage((p) => Math.min(usersTotalPages, p + 1))}
              disabled={pickerPage >= usersTotalPages || usersLoading}
              className="p-1 rounded hover:bg-primary/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="h-3.5 w-3.5 text-foreground" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Send Notification Tab
// ============================================================================

const SendTab: React.FC<{ onSent: () => void }> = ({ onSent }) => {
  const { templates, loading: templatesLoading } = useNotificationTemplates();

  const [templateKey, setTemplateKey] = useState('');
  const [customTitle, setCustomTitle] = useState('');
  const [customBody, setCustomBody] = useState('');
  const [channels, setChannels] = useState<NotificationChannel[]>([]);
  const [sendMode, setSendMode] = useState<SendMode>('users');
  const [selectedUsers, setSelectedUsers] = useState<SelectedUser[]>([]);
  const [sending, setSending] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.key === templateKey),
    [templates, templateKey],
  );

  const handleTemplateChange = useCallback((key: string) => {
    setTemplateKey(key);
    const t = templates.find((tpl) => tpl.key === key);
    if (t) {
      setCustomTitle(t.title);
      setCustomBody(t.body);
    }
  }, [templates]);

  const toggleChannel = useCallback((ch: NotificationChannel) => {
    setChannels((prev) =>
      prev.includes(ch) ? prev.filter((c) => c !== ch) : [...prev, ch]
    );
  }, []);

  const addUser = useCallback((user: SelectedUser) => {
    setSelectedUsers((prev) => {
      if (prev.some((u) => u.id === user.id)) return prev;
      return [...prev, user];
    });
  }, []);

  const removeUser = useCallback((userId: number) => {
    setSelectedUsers((prev) => prev.filter((u) => u.id !== userId));
  }, []);

  const canSend = templateKey && channels.length > 0 && (
    (sendMode === 'users' && selectedUsers.length > 0) ||
    sendMode === 'broadcast'
  );

  const handleSend = useCallback(async () => {
    setSending(true);
    try {
      const overrides: { title?: string; body?: string } = {};
      if (selectedTemplate && customTitle !== selectedTemplate.title) overrides.title = customTitle;
      if (selectedTemplate && customBody !== selectedTemplate.body) overrides.body = customBody;

      if (sendMode === 'users' && selectedUsers.length === 1) {
        const reqBody: SendToUserRequest = {
          userId: selectedUsers[0].id,
          templateKey: templateKey || undefined,
          channels,
          ...overrides,
        };
        await notificationsService.sendToUser(reqBody);
      } else if (sendMode === 'users') {
        const reqBody: SendToMultipleRequest = {
          userIds: selectedUsers.map((u) => u.id),
          templateKey: templateKey || undefined,
          channels,
          ...overrides,
        };
        await notificationsService.sendToMultiple(reqBody);
      } else {
        const reqBody: SendBroadcastRequest = {
          templateKey: templateKey || undefined,
          channels,
          ...overrides,
        };
        await notificationsService.sendBroadcast(reqBody);
      }

      showToast('success', `Notification${selectedUsers.length > 1 || sendMode === 'broadcast' ? 's' : ''} sent successfully`);
      setShowConfirm(false);
      setTemplateKey('');
      setCustomTitle('');
      setCustomBody('');
      setChannels([]);
      setSelectedUsers([]);
      onSent();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send notification';
      showToast('error', message);
    } finally {
      setSending(false);
    }
  }, [sendMode, templateKey, channels, customTitle, customBody, selectedUsers, selectedTemplate, onSent]);

  return (
    <div className="flex gap-6 h-full">
      {/* Left column — Form */}
      <div className={cn('space-y-5 overflow-y-auto', sendMode === 'users' ? 'flex-1 min-w-0' : 'max-w-2xl w-full')}>
        {/* Template Picker */}
        <div className="card p-4 space-y-3">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Template</h3>
          {templatesLoading ? (
            <div className="flex items-center gap-2 py-2"><Loader2 className="h-4 w-4 text-primary animate-spin" /><span className="text-xs text-muted-foreground">Loading templates...</span></div>
          ) : (
            <select
              value={templateKey}
              onChange={(e) => handleTemplateChange(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
            >
              <option value="">Select a template...</option>
              {templates.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.key}{t.category ? ` (${t.category})` : ''}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Custom Title & Body */}
        <div className="card p-4 space-y-3">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Content</h3>
          <div>
            <label className="block text-[10px] text-muted-foreground mb-1">Title</label>
            <input
              type="text"
              value={customTitle}
              onChange={(e) => setCustomTitle(e.target.value)}
              placeholder="Notification title..."
              className="w-full px-3 py-2 text-sm bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
            />
          </div>
          <div>
            <label className="block text-[10px] text-muted-foreground mb-1">Body</label>
            <textarea
              value={customBody}
              onChange={(e) => setCustomBody(e.target.value)}
              placeholder="Notification body..."
              rows={3}
              className="w-full px-3 py-2 text-sm bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 resize-none"
            />
          </div>
          {selectedTemplate && (customTitle !== selectedTemplate.title || customBody !== selectedTemplate.body) && (
            <p className="text-[10px] text-yellow-400 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              Content differs from template defaults — custom overrides will be sent.
            </p>
          )}
        </div>

        {/* Channel Selection */}
        <div className="card p-4 space-y-3">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Channels</h3>
          <div className="flex flex-wrap gap-2">
            {ALL_CHANNELS.map(({ key, label, icon: Icon }) => {
              const selected = channels.includes(key);
              return (
                <button
                  key={key}
                  onClick={() => toggleChannel(key)}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 text-xs rounded-lg border transition-all',
                    selected
                      ? 'bg-primary/15 text-primary border-primary/30 font-medium'
                      : 'bg-card text-muted-foreground border-border hover:bg-primary/10 hover:text-foreground'
                  )}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Send Mode Toggle */}
        <div className="card p-4 space-y-3">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Recipients</h3>
          <div className="flex items-center gap-1 bg-card border border-border rounded-lg p-1 w-fit">
            {([
              ['users', 'Select Users', Users],
              ['broadcast', 'Broadcast', Radio],
            ] as const).map(([key, label, Icon]) => (
              <button
                key={key}
                onClick={() => setSendMode(key)}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md transition-colors',
                  sendMode === key
                    ? 'bg-primary/20 text-primary font-medium'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <Icon className="h-3.5 w-3.5" />{label}
              </button>
            ))}
          </div>

          {sendMode === 'users' && selectedUsers.length > 0 && (
            <div>
              <p className="text-[10px] text-muted-foreground mb-1.5">{selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected</p>
              <div className="flex flex-wrap gap-1.5">
                {selectedUsers.map((u) => (
                  <span
                    key={u.id}
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] bg-primary/15 text-primary border border-primary/30 rounded-full"
                  >
                    {u.name || u.email || `#${u.id}`}
                    <button onClick={() => removeUser(u.id)} className="hover:text-red-400 transition-colors">
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {sendMode === 'users' && selectedUsers.length === 0 && (
            <p className="text-[10px] text-muted-foreground flex items-center gap-1.5">
              <Users className="h-3 w-3" />
              Use the panel on the right to search and select users.
            </p>
          )}

          {sendMode === 'broadcast' && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-red-400 flex-shrink-0" />
              <p className="text-[10px] text-red-400">
                Broadcast will send to <strong>all users</strong>. Use with caution.
              </p>
            </div>
          )}
        </div>

        {/* Send Button */}
        <button
          onClick={() => setShowConfirm(true)}
          disabled={!canSend}
          className={cn(
            'w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium rounded-lg transition-all',
            canSend
              ? 'bg-primary/20 text-primary hover:bg-primary/30 border border-primary/30'
              : 'bg-muted/20 text-muted-foreground cursor-not-allowed border border-border'
          )}
        >
          <Send className="h-4 w-4" />
          Send Notification
        </button>
      </div>

      {/* Right column — User Picker (visible only in users mode) */}
      {sendMode === 'users' && (
        <div className="w-80 xl:w-96 flex-shrink-0 hidden md:flex">
          <UserPickerPanel
            selectedUsers={selectedUsers}
            onAdd={addUser}
            onRemove={removeUser}
          />
        </div>
      )}

      {/* Mobile fallback: show picker below form when in users mode on small screens */}
      {sendMode === 'users' && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-30 h-[50vh] border-t border-border bg-background">
          <UserPickerPanel
            selectedUsers={selectedUsers}
            onAdd={addUser}
            onRemove={removeUser}
          />
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        open={showConfirm}
        title="Confirm Send"
        confirmLabel={sending ? 'Sending...' : 'Send Now'}
        confirmVariant={sendMode === 'broadcast' ? 'danger' : 'primary'}
        loading={sending}
        onConfirm={handleSend}
        onCancel={() => setShowConfirm(false)}
      >
        <p>Template: <span className="text-foreground font-medium">{templateKey}</span></p>
        <p>Channels: <span className="text-foreground font-medium">{channels.join(', ')}</span></p>
        <p>
          Target:{' '}
          <span className="text-foreground font-medium">
            {sendMode === 'users' && selectedUsers.length === 1 && (selectedUsers[0].name || `User #${selectedUsers[0].id}`)}
            {sendMode === 'users' && selectedUsers.length > 1 && `${selectedUsers.length} users`}
            {sendMode === 'broadcast' && 'All users (broadcast)'}
          </span>
        </p>
        {sendMode === 'users' && selectedUsers.length > 1 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {selectedUsers.slice(0, 8).map((u) => (
              <span key={u.id} className="text-[10px] bg-muted/20 text-foreground px-1.5 py-0.5 rounded">
                {u.name || u.email || `#${u.id}`}
              </span>
            ))}
            {selectedUsers.length > 8 && (
              <span className="text-[10px] text-muted-foreground">+{selectedUsers.length - 8} more</span>
            )}
          </div>
        )}
        {sendMode === 'broadcast' && (
          <div className="flex items-center gap-2 p-2 mt-2 bg-red-500/10 border border-red-500/20 rounded-lg">
            <AlertTriangle className="h-3.5 w-3.5 text-red-400 flex-shrink-0" />
            <p className="text-[10px] text-red-400 font-medium">
              This will send to ALL users. This action cannot be undone.
            </p>
          </div>
        )}
      </ConfirmModal>
    </div>
  );
};

// ============================================================================
// History Tab
// ============================================================================

const HistoryTab: React.FC<{ refreshKey: number }> = ({ refreshKey }) => {
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [batchInfo, setBatchInfo] = useState<{ batchId: string; data: BatchSummary | null; loading: boolean } | null>(null);

  const params = useMemo(() => ({
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  }), [page]);

  const { items, total, totalPages, loading, page: currentPage, refetch } = useNotificationHistory(params);

  // Re-fetch when refreshKey changes (after sending)
  React.useEffect(() => {
    if (refreshKey > 0) refetch();
  }, [refreshKey, refetch]);

  // Compute stat summary from current page
  const stats = useMemo(() => {
    const s = { sent: 0, delivered: 0, failed: 0, pending: 0 };
    items.forEach((item) => {
      const st = item.status?.toLowerCase();
      if (st === 'sent') s.sent++;
      else if (st === 'delivered' || st === 'read') s.delivered++;
      else if (st === 'failed') s.failed++;
      else s.pending++;
    });
    return s;
  }, [items]);

  const handleBatchClick = useCallback(async (batchId: string) => {
    if (batchInfo?.batchId === batchId) {
      setBatchInfo(null);
      return;
    }
    setBatchInfo({ batchId, data: null, loading: true });
    try {
      const data = await notificationsService.getBatchSummary(batchId);
      setBatchInfo({ batchId, data, loading: false });
    } catch {
      setBatchInfo({ batchId, data: null, loading: false });
      showToast('error', 'Failed to load batch summary');
    }
  }, [batchInfo?.batchId]);

  return (
    <div className="space-y-4">
      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Sent" value={formatNumber(stats.sent)} icon={Send} color="text-blue-400" bgColor="bg-blue-400/10" loading={loading} />
        <StatCard label="Delivered" value={formatNumber(stats.delivered)} icon={CheckCircle} color="text-emerald-400" bgColor="bg-emerald-400/10" loading={loading} />
        <StatCard label="Failed" value={formatNumber(stats.failed)} icon={XCircle} color="text-red-400" bgColor="bg-red-400/10" loading={loading} />
        <StatCard label="Pending" value={formatNumber(stats.pending)} icon={Clock} color="text-yellow-400" bgColor="bg-yellow-400/10" loading={loading} />
      </div>

      {/* Batch summary card */}
      {batchInfo && (
        <div className="card p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-semibold text-muted-foreground">Batch Summary</h4>
            <button onClick={() => setBatchInfo(null)} className="p-1 rounded hover:bg-primary/10 transition-colors">
              <X className="h-3 w-3 text-muted-foreground" />
            </button>
          </div>
          {batchInfo.loading ? (
            <div className="flex items-center gap-2 py-2"><Loader2 className="h-4 w-4 text-primary animate-spin" /></div>
          ) : batchInfo.data ? (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <div className="text-center p-2 bg-muted/10 rounded-lg">
                <p className="text-sm font-bold text-foreground">{batchInfo.data.total}</p>
                <p className="text-[10px] text-muted-foreground">Total</p>
              </div>
              <div className="text-center p-2 bg-muted/10 rounded-lg">
                <p className="text-sm font-bold text-blue-400">{batchInfo.data.sent}</p>
                <p className="text-[10px] text-muted-foreground">Sent</p>
              </div>
              <div className="text-center p-2 bg-muted/10 rounded-lg">
                <p className="text-sm font-bold text-emerald-400">{batchInfo.data.delivered}</p>
                <p className="text-[10px] text-muted-foreground">Delivered</p>
              </div>
              <div className="text-center p-2 bg-muted/10 rounded-lg">
                <p className="text-sm font-bold text-red-400">{batchInfo.data.failed}</p>
                <p className="text-[10px] text-muted-foreground">Failed</p>
              </div>
              <div className="text-center p-2 bg-muted/10 rounded-lg">
                <p className="text-sm font-bold text-yellow-400">{batchInfo.data.pending}</p>
                <p className="text-[10px] text-muted-foreground">Pending</p>
              </div>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Could not load batch summary.</p>
          )}
        </div>
      )}

      {/* History list */}
      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 text-primary animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground text-sm">No notification history found</div>
      ) : (
        <div className="space-y-2">
          {items.map((entry) => (
            <HistoryRow
              key={entry.id}
              entry={entry}
              expanded={expandedId === entry.id}
              onToggle={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
              onBatchClick={handleBatchClick}
            />
          ))}
        </div>
      )}
      <Pagination page={currentPage} totalPages={totalPages} total={total} onPageChange={setPage} loading={loading} />
    </div>
  );
};

// ============================================================================
// History Row
// ============================================================================

const HistoryRow: React.FC<{
  entry: NotificationLogEntry;
  expanded: boolean;
  onToggle: () => void;
  onBatchClick: (batchId: string) => void;
}> = ({ entry, expanded, onToggle, onBatchClick }) => {
  // Extract error from channelResults if present
  const errorMsg = entry.channelResults?.error as string | undefined;
  const sentByLabel = entry.sentByAdmin?.name || (entry.sentByAdminId ? `Admin #${entry.sentByAdminId}` : 'System');

  return (
    <div
      className={cn('card p-3 cursor-pointer transition-all', expanded && 'ring-1 ring-primary/30')}
      onClick={onToggle}
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="flex-shrink-0">
            {expanded ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
          </div>
          <div className="flex-shrink-0 w-16 text-xs text-muted-foreground">
            {new Date(entry.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </div>
          <span className="text-xs text-foreground truncate max-w-[200px]">
            {entry.recipientUser?.name || `User #${entry.recipientUserId}`}
          </span>
          {entry.templateKey && (
            <span className="text-[10px] font-mono text-muted-foreground truncate max-w-[120px]">{entry.templateKey}</span>
          )}
          <span className="text-xs text-foreground truncate max-w-[180px]">{entry.title}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-muted-foreground whitespace-nowrap">by {sentByLabel}</span>
          {entry.channels.map((ch) => (
            <Badge key={ch} variant={channelBadge(ch).variant} size="sm">{ch}</Badge>
          ))}
          <Badge variant={statusBadge(entry.status).variant} size="sm">{entry.status}</Badge>
        </div>
      </div>

      {errorMsg && (
        <p className="text-[10px] text-red-400 mt-1 flex items-center gap-1 ml-7 line-clamp-1">
          <AlertTriangle className="h-3 w-3 flex-shrink-0" />{errorMsg}
        </p>
      )}

      {expanded && (
        <div className="border-t border-border mt-3 pt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
          <DetailSection title="Content">
            <DetailRow label="Title" value={entry.title} />
            <div className="col-span-full">
              <span className="text-[10px] text-muted-foreground">Body</span>
              <p className="text-[10px] text-foreground mt-0.5">{entry.body}</p>
            </div>
          </DetailSection>
          <DetailSection title="Delivery">
            <DetailRow label="Channels" value={entry.channels.join(', ')} />
            <DetailRow label="Status" value={entry.status} />
            <DetailRow label="Template" value={entry.templateKey} />
            {errorMsg && <DetailRow label="Error" value={<span className="text-red-400 break-all">{errorMsg}</span>} />}
            {/* Show per-channel results */}
            {entry.channelResults && !entry.channelResults.error && Object.entries(entry.channelResults).map(([ch, result]) => (
              <DetailRow
                key={ch}
                label={ch.toUpperCase()}
                value={typeof result === 'object' && result !== null && 'success' in result
                  ? (result as { success: boolean }).success ? 'Success' : 'Failed'
                  : String(result)}
              />
            ))}
          </DetailSection>
          <DetailSection title="Recipient">
            <DetailRow label="User ID" value={String(entry.recipientUserId)} />
            <DetailRow label="Name" value={entry.recipientUser?.name} />
            <DetailRow label="Email" value={entry.recipientUser?.email} />
            <DetailRow label="Phone" value={entry.recipientUser?.phoneNumber} />
          </DetailSection>
          <DetailSection title="Sent By">
            <DetailRow label="Admin ID" value={entry.sentByAdminId != null ? String(entry.sentByAdminId) : undefined} />
            <DetailRow label="Name" value={entry.sentByAdmin?.name || sentByLabel} />
          </DetailSection>
          <DetailSection title="Timeline">
            <DetailRow label="Created" value={formatDateTime(entry.createdAt)} />
          </DetailSection>
          {entry.batchId && (
            <DetailSection title="Batch">
              <div className="col-span-full">
                <button
                  onClick={(e) => { e.stopPropagation(); onBatchClick(entry.batchId!); }}
                  className="inline-flex items-center gap-1 text-[10px] font-mono text-primary bg-primary/10 px-2 py-1 rounded cursor-pointer hover:bg-primary/20 transition-colors"
                >
                  <Copy className="h-2.5 w-2.5" />
                  {entry.batchId.length > 24 ? entry.batchId.slice(0, 10) + '...' + entry.batchId.slice(-6) : entry.batchId}
                  <span className="text-muted-foreground ml-1">View batch summary</span>
                </button>
              </div>
            </DetailSection>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Main Notifications Page
// ============================================================================

const Notifications: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>('send');
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);

  const handleSent = useCallback(() => {
    setHistoryRefreshKey((k) => k + 1);
    setActiveTab('history');
  }, []);

  const TABS: { key: TabKey; label: string; icon: React.ElementType }[] = [
    { key: 'send', label: 'Send Notification', icon: Send },
    { key: 'history', label: 'History', icon: History },
  ];

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Tab bar */}
      <div className="flex-shrink-0 border-b border-border bg-card">
        <div className="flex items-center gap-1 px-4 py-2 overflow-x-auto scrollbar-thin">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-all whitespace-nowrap',
                  isActive
                    ? 'bg-primary/15 text-primary font-medium border border-primary/30'
                    : 'text-muted-foreground hover:bg-primary/10 hover:text-foreground'
                )}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-4 lg:p-6">
        {activeTab === 'send' && <SendTab onSent={handleSent} />}
        {activeTab === 'history' && <HistoryTab refreshKey={historyRefreshKey} />}
      </div>
    </div>
  );
};

export default Notifications;
